import { useMemo, useState, useTransition } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { fmtDate, fmtDateTime } from "@/lib/dates";
import { Glyph, Modal } from "@/features/warehouse/WarehouseParts";
import {
  PARTNER_LANGUAGES,
  invitePartnerUsers,
  listPartnerUsers,
  lookupPartnerUsers,
  partnerUserKeys,
  type BatchResult,
  type KnownPartnerUser,
  type PartnerLanguage,
} from "@/features/parties/partnerUsers.api";
import { isEmail, isStale, isWillems, parsePeople } from "./pastedPeople";

// The people of one customer at once (H-b04; docs/partner-portal.md §15, T7): pasted from the export of the TALIX
// Portal or added one by one, ticked, the language checked, sent. An address on the portal already is linked, not
// invited; somebody of Willems itself and who has not been in the TALIX Portal for half a year start unticked. Each
// line gets its own outcome; nothing of the pasted list is kept.

const LANGUAGE_LABEL_KEYS: Record<PartnerLanguage, string> = { nl: "portal.language.nl", en: "portal.language.en", de: "portal.language.de" };

interface Row {
  key: number;
  name: string;
  email: string;
  language: PartnerLanguage;
  lastLogin: Date | null;
  /** Typed here rather than pasted: name and address stay editable. */
  manual: boolean;
  /** The user's own tick; null follows the default. */
  ticked: boolean | null;
}

type Why = "invalid_email" | "invalid_name" | "duplicate" | "on_relation" | "willems" | "stale" | null;

let nextKey = 1;

export function InvitePeopleModal({
  party,
  onClose,
  onDone,
}: {
  party: { id: string; code: string; name: string };
  onClose: () => void;
  /** After sending, when the outcome is closed: the lists to load again. */
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Row[]>([]);
  const [paste, setPaste] = useState("");
  const [pasteOpen, setPasteOpen] = useState(true);
  const [pasteNote, setPasteNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BatchResult | null>(null);
  const [isPending, startTransition] = useTransition();

  const users = useQuery({ queryKey: partnerUserKeys.byParty(party.id), queryFn: () => listPartnerUsers(party.id) });
  const mail = users.data?.mail;
  const canSend = mail ? mail.configured || mail.linksShown : false;

  const emails = useMemo(() => [...new Set(rows.map((row) => row.email.trim().toLowerCase()).filter(isEmail))].sort(), [rows]);
  const lookup = useQuery({
    queryKey: ["partner-users-lookup", party.id, emails],
    queryFn: () => lookupPartnerUsers(party.id, emails),
    enabled: emails.length > 0,
    staleTime: 60_000,
  });
  const known = new Map<string, KnownPartnerUser>((lookup.data?.people ?? []).map((person) => [person.email, person]));

  // Why a line starts unticked or cannot go: the first that applies.
  const seen = new Set<string>();
  const why = new Map<number, Why>();
  for (const row of rows) {
    const email = row.email.trim().toLowerCase();
    const person = known.get(email);
    let reason: Why = null;
    if (!isEmail(email)) reason = "invalid_email";
    else if (seen.has(email)) reason = "duplicate";
    else if (person?.onThisRelation) reason = "on_relation";
    else if (!person && row.name.trim().length < 2) reason = "invalid_name";
    else if (isWillems(email)) reason = "willems";
    else if (isStale(row.lastLogin)) reason = "stale";
    if (isEmail(email)) seen.add(email);
    why.set(row.key, reason);
  }
  const blocked = (reason: Why) => reason === "invalid_email" || reason === "invalid_name" || reason === "duplicate" || reason === "on_relation";
  const ticked = (row: Row) => {
    const reason = why.get(row.key) ?? null;
    if (blocked(reason)) return false;
    return row.ticked ?? (reason === null);
  };
  const chosen = rows.filter(ticked);
  const links = chosen.filter((row) => known.has(row.email.trim().toLowerCase())).length;
  const invitations = chosen.length - links;

  function take() {
    const pasted = parsePeople(paste, party.code);
    setRows((prev) => [
      ...prev,
      ...pasted.people.map((person) => ({
        key: nextKey++,
        name: person.name,
        email: person.email,
        language: person.language ?? "nl",
        lastLogin: person.lastLogin,
        manual: false,
        ticked: null,
      })),
    ]);
    const parts = [t("portal.batch.taken", { count: pasted.people.length })];
    if (pasted.otherCustomer > 0) parts.push(t("portal.batch.otherCustomer", { count: pasted.otherCustomer }));
    if (pasted.unreadable > 0) parts.push(t("portal.batch.unreadable", { count: pasted.unreadable }));
    setPasteNote(parts.join(" · "));
    if (pasted.people.length > 0) {
      setPaste("");
      setPasteOpen(false);
    }
  }

  function addPerson() {
    setRows((prev) => [...prev, { key: nextKey++, name: "", email: "", language: "nl", lastLogin: null, manual: true, ticked: true }]);
  }

  function change(key: number, patch: Partial<Row>) {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  function send() {
    setError(null);
    startTransition(async () => {
      try {
        setResult(
          await invitePartnerUsers(
            party.id,
            chosen.map((row) => ({ email: row.email.trim(), displayName: row.name.trim(), language: row.language })),
          ),
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : t("common.errorGeneric"));
      }
    });
  }

  const title = t("portal.batch.title", { party: party.name });

  if (result) {
    return (
      <Modal title={title} onClose={onDone} wide>
        <Outcome result={result} onClose={onDone} />
      </Modal>
    );
  }

  const allTicked = rows.length > 0 && rows.every((row) => ticked(row) || blocked(why.get(row.key) ?? null));

  return (
    <Modal title={title} onClose={onClose} wide>
      <div className="wl-modal__body">
        <div className="wl-vstack" style={{ gap: "var(--space-3)", alignItems: "stretch", padding: "var(--space-2) var(--space-5) var(--space-4)" }}>
          <p className="t-body-sm t-muted" style={{ margin: 0 }}>
            {t("portal.batch.explain")}
          </p>
          {mail && !canSend && (
            <div className="wl-alert wl-alert--warning">
              <span>{t("portal.mailOff")}</span>
            </div>
          )}

          {rows.length > 0 && (
            <div className="wl-table-wrap">
              <table className="wl-table">
                <thead>
                  <tr>
                    <th style={{ width: "var(--space-8)" }}>
                      <input
                        type="checkbox"
                        aria-label={t("portal.batch.tickAll")}
                        checked={allTicked}
                        onChange={(e) => setRows((prev) => prev.map((row) => ({ ...row, ticked: e.target.checked })))}
                      />
                    </th>
                    <th>{t("portal.colName")}</th>
                    <th>{t("portal.colEmail")}</th>
                    <th style={{ width: "120px" }}>{t("portal.colLanguage")}</th>
                    <th style={{ width: "110px" }}>{t("portal.batch.colLastTalix")}</th>
                    <th style={{ width: "var(--space-8)" }} aria-label={t("portal.batch.remove")} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const reason = why.get(row.key) ?? null;
                    const person = known.get(row.email.trim().toLowerCase());
                    return (
                      <tr key={row.key}>
                        <td>
                          <input
                            type="checkbox"
                            aria-label={row.name || row.email}
                            checked={ticked(row)}
                            disabled={blocked(reason)}
                            onChange={(e) => change(row.key, { ticked: e.target.checked })}
                          />
                        </td>
                        <td>
                          {row.manual ? (
                            <input
                              className="wl-input"
                              style={{ height: "var(--control-height-sm)" }}
                              value={row.name}
                              maxLength={100}
                              aria-label={t("portal.colName")}
                              autoFocus={!row.name && !row.email}
                              onChange={(e) => change(row.key, { name: e.target.value })}
                            />
                          ) : (
                            row.name || <span className="t-muted">—</span>
                          )}
                        </td>
                        <td>
                          {row.manual ? (
                            <input
                              className="wl-input mono"
                              style={{ height: "var(--control-height-sm)" }}
                              type="email"
                              value={row.email}
                              maxLength={254}
                              aria-label={t("portal.colEmail")}
                              aria-invalid={(row.email.trim() !== "" && reason === "invalid_email") || undefined}
                              onChange={(e) => change(row.key, { email: e.target.value })}
                            />
                          ) : (
                            <span className="mono">{row.email}</span>
                          )}
                          <Note reason={reason} person={person} />
                        </td>
                        <td>
                          <select
                            className="wl-select"
                            style={{ height: "var(--control-height-sm)" }}
                            value={row.language}
                            aria-label={t("portal.colLanguage")}
                            disabled={Boolean(person)}
                            onChange={(e) => change(row.key, { language: e.target.value as PartnerLanguage })}
                          >
                            {PARTNER_LANGUAGES.map((option) => (
                              <option key={option} value={option}>
                                {t(LANGUAGE_LABEL_KEYS[option] as never)}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="mono">{row.lastLogin ? fmtDate(row.lastLogin) : "—"}</td>
                        <td>
                          <button
                            type="button"
                            className="wl-btn wl-btn--ghost wl-btn--icon wl-btn--sm"
                            aria-label={t("portal.batch.remove")}
                            onClick={() => setRows((prev) => prev.filter((other) => other.key !== row.key))}
                          >
                            <Glyph name="close" size={13} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="wl-hstack" style={{ gap: "var(--space-3)", alignItems: "center", flexWrap: "wrap" }}>
            <button type="button" className="wl-btn wl-btn--ghost wl-btn--sm" onClick={addPerson}>
              <Glyph name="plus" size={13} /> {t("portal.batch.addPerson")}
            </button>
            <span className="wl-spacer" />
            {!pasteOpen && (
              <button type="button" className="wl-btn wl-btn--ghost wl-btn--sm" onClick={() => setPasteOpen(true)}>
                {t("portal.batch.pasteOpen")}
              </button>
            )}
          </div>

          {pasteOpen && (
            <div className="wl-field">
              <label className="wl-field__label" htmlFor="portal-paste">
                {t("portal.batch.pasteLabel")}
              </label>
              <textarea
                id="portal-paste"
                className="wl-textarea mono"
                rows={5}
                value={paste}
                placeholder={t("portal.batch.pastePlaceholder", { code: party.code })}
                onChange={(e) => setPaste(e.target.value)}
              />
              <div className="wl-hstack" style={{ gap: "var(--space-3)", alignItems: "center", marginTop: "var(--space-2)" }}>
                <span className="wl-field__help" style={{ margin: 0 }}>
                  {t("portal.batch.pasteHelp", { code: party.code })}
                </span>
                <span className="wl-spacer" />
                <button type="button" className="wl-btn wl-btn--secondary wl-btn--sm" disabled={!paste.trim()} onClick={take}>
                  {t("portal.batch.take")}
                </button>
              </div>
            </div>
          )}
          {pasteNote && <span className="t-body-sm t-muted">{pasteNote}</span>}

          {error && (
            <div className="wl-alert wl-alert--danger" role="alert">
              <span>{error}</span>
            </div>
          )}

          <div className="wl-hstack" style={{ gap: "var(--space-2)", alignItems: "center", borderTop: "1px solid var(--border-subtle)", paddingTop: "var(--space-3)" }}>
            <span className="t-body-sm t-muted">
              {[
                t("portal.batch.invitations", { count: invitations }),
                ...(links > 0 ? [t("portal.batch.links", { count: links })] : []),
              ].join(" · ")}
            </span>
            <span className="wl-spacer" />
            <button type="button" className="wl-btn wl-btn--secondary" onClick={onClose}>
              {t("common.cancel")}
            </button>
            <button type="button" className="wl-btn wl-btn--primary" disabled={isPending || !canSend || chosen.length === 0 || lookup.isFetching} onClick={send}>
              {isPending ? t("common.saving") : t("portal.batch.send", { count: chosen.length })}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function Note({ reason, person }: { reason: Why; person: KnownPartnerUser | undefined }) {
  const { t } = useTranslation();
  let text: string | null = null;
  let danger = false;
  if (reason === "invalid_email") [text, danger] = [t("portal.batch.why.invalidEmail"), true];
  else if (reason === "invalid_name") [text, danger] = [t("portal.batch.why.invalidName"), true];
  else if (reason === "duplicate") text = t("portal.batch.why.duplicate");
  else if (reason === "on_relation") text = t("portal.batch.why.onRelation");
  else if (reason === "willems") text = t("portal.batch.why.willems");
  else if (reason === "stale") text = t("portal.batch.why.stale");
  else if (person) text = t("portal.batch.why.linked", { count: person.parties.length });
  if (!text) return null;
  const warn = reason === "stale" || reason === "willems";
  return (
    <div
      className="t-caption"
      style={{ color: danger ? "var(--text-danger)" : warn ? "var(--text-warning)" : "var(--text-secondary)", marginTop: "var(--space-0_5)" }}
    >
      {text}
    </div>
  );
}

const OUTCOME_KEYS: Record<string, string> = {
  invited: "portal.batch.outcome.invited",
  linked: "portal.batch.outcome.linked",
  exists: "portal.batch.outcome.exists",
  failed: "portal.batch.outcome.failed",
};

const REASON_KEYS: Record<string, string> = {
  invalid_email: "portal.batch.why.invalidEmail",
  invalid_name: "portal.batch.why.invalidName",
  duplicate: "portal.batch.why.duplicate",
  error: "portal.batch.reasonError",
};

function Outcome({ result, onClose }: { result: BatchResult; onClose: () => void }) {
  const { t } = useTranslation();
  const counts = result.counts;
  const summary = [
    counts.invited > 0 && t("portal.batch.done.invited", { count: counts.invited }),
    counts.linked > 0 && t("portal.batch.done.linked", { count: counts.linked }),
    counts.exists > 0 && t("portal.batch.done.exists", { count: counts.exists }),
    counts.failed > 0 && t("portal.batch.done.failed", { count: counts.failed }),
  ].filter(Boolean);
  return (
    <div className="wl-modal__body">
      <div className="wl-vstack" style={{ gap: "var(--space-3)", alignItems: "stretch", padding: "var(--space-2) var(--space-5) var(--space-4)" }}>
        <p className="t-body-sm" style={{ margin: 0 }}>
          {summary.join(" · ")}
        </p>
        <div className="wl-table-wrap">
          <table className="wl-table">
            <thead>
              <tr>
                <th>{t("portal.colName")}</th>
                <th>{t("portal.colEmail")}</th>
                <th>{t("portal.batch.colOutcome")}</th>
              </tr>
            </thead>
            <tbody>
              {result.results.map((line, index) => (
                <tr key={`${line.email}-${index}`}>
                  <td>{line.user?.name ?? "—"}</td>
                  <td className="mono">{line.email}</td>
                  <td>
                    <span style={{ color: line.outcome === "failed" ? "var(--text-danger)" : undefined }}>
                      {t(OUTCOME_KEYS[line.outcome] as never)}
                      {line.reason && `: ${t((REASON_KEYS[line.reason] ?? "portal.batch.reasonError") as never)}`}
                    </span>
                    {line.invitation && !line.invitation.mailed && !line.invitation.link && (
                      <div className="t-caption" style={{ color: "var(--text-warning)" }}>
                        {t("portal.batch.notMailed")}
                      </div>
                    )}
                    {line.invitation && (
                      <div className="t-caption t-muted">{t("portal.batch.until", { until: fmtDateTime(new Date(line.invitation.expiresAt)) })}</div>
                    )}
                    {line.invitation?.link && (
                      <input className="wl-input mono" style={{ height: "var(--control-height-sm)", marginTop: "var(--space-1)" }} value={line.invitation.link} readOnly onFocus={(e) => e.target.select()} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="wl-hstack" style={{ justifyContent: "flex-end" }}>
          <button type="button" className="wl-btn wl-btn--primary" onClick={onClose}>
            {t("portal.done")}
          </button>
        </div>
      </div>
    </div>
  );
}
